import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Bell, ExternalLink } from "lucide-react";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { playFlashAlarm, vibrateFlash } from "../lib/flashAlert";

/**
 * Global listener that reacts to `admin_flash` WebSocket messages and
 * displays them as a centered modal. The websocket is already opened by
 * `useWebSocketNotifications` which dispatches a `trigo:ws-message`
 * CustomEvent on the window. We simply subscribe to that event, avoiding
 * a second WebSocket connection.
 */
export default function AdminFlashModal() {
  const { user } = useAuth();
  const [flash, setFlash] = useState(null);
  const [queue, setQueue] = useState([]); // extras pulled from /flash/pending
  const [seenIds] = useState(() => new Set());

  // Show the next queued flash whenever the current one is dismissed.
  useEffect(() => {
    if (!flash && queue.length > 0) {
      const [next, ...rest] = queue;
      setQueue(rest);
      const silent = !!user?.silent_notifications;
      playFlashAlarm({ silent });
      vibrateFlash({ silent });
      setFlash(next);
    }
  }, [flash, queue, user?.silent_notifications]);

  // On login/app-open, fetch flashes the user missed while offline.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    api.get("/flash/pending").then(({ data }) => {
      if (cancelled) return;
      const items = (Array.isArray(data) ? data : []).filter((m) => m?.id && !seenIds.has(m.id));
      items.forEach((m) => seenIds.add(m.id));
      if (items.length === 0) return;
      // Display the most recent one immediately, queue the rest.
      const [first, ...rest] = items;
      const silent = !!user?.silent_notifications;
      playFlashAlarm({ silent });
      vibrateFlash({ silent });
      setFlash(first);
      if (rest.length) setQueue((q) => [...q, ...rest]);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [user?.id, user?.silent_notifications, seenIds]);

  useEffect(() => {
    const handler = (evt) => {
      const msg = evt.detail;
      if (msg?.type !== "admin_flash") return;
      if (msg.id && seenIds.has(msg.id)) return;
      if (msg.id) seenIds.add(msg.id);
      // Prominent audio + vibration alert, silenced per-user preference.
      const silent = !!user?.silent_notifications;
      playFlashAlarm({ silent });
      vibrateFlash({ silent });
      setFlash(msg);
    };
    window.addEventListener("trigo:ws-message", handler);
    return () => window.removeEventListener("trigo:ws-message", handler);
  }, [seenIds, user?.silent_notifications]);

  if (!flash) return null;

  const handleClose = () => {
    // Mark seen on backend so it doesn't reappear on next app open.
    if (flash.id) api.post(`/flash/${flash.id}/seen`).catch(() => { /* silent */ });
    setFlash(null);
  };
  const handleCta = () => {
    // Track the click for analytics — best effort, fire and forget.
    if (flash.id) {
      api.post(`/admin/broadcast/flash/${flash.id}/click`).catch(() => { /* silent */ });
      // Also mark as seen so it doesn't reopen on next app open.
      api.post(`/flash/${flash.id}/seen`).catch(() => { /* silent */ });
    }
    if (flash.cta_url) {
      if (flash.cta_url.startsWith("http")) {
        window.open(flash.cta_url, "_blank");
      } else {
        window.location.href = flash.cta_url;
      }
    }
    handleClose();
  };

  return (
    <Dialog open={!!flash} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-0"
        data-testid="admin-flash-modal"
      >
        <VisuallyHidden.Root>
          <DialogTitle>{flash.title || "Message TrigoTour"}</DialogTitle>
          <DialogDescription>{flash.body || "Notification de la plateforme."}</DialogDescription>
        </VisuallyHidden.Root>
        <div className="bg-gradient-to-br from-[#0A2540] to-[#0F2E4E] text-white p-6" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
          {/* Close — Radix DialogContent already renders its own X (absolute
              top-4 right-4), so we do NOT add a custom one to avoid the two
              buttons overlapping (which intercepts clicks). Dismissal is wired
              via onOpenChange → handleClose on the parent Dialog. */}

          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-[#FACC15] flex items-center justify-center shrink-0">
              <Bell className="w-5 h-5 text-[#0A2540]" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FACC15]">
                Message TrigoTour
              </p>
              <h2 className="mt-1 text-xl font-extrabold" data-testid="admin-flash-title" style={{ fontFamily: "Outfit" }}>
                {flash.title}
              </h2>
            </div>
          </div>

          {flash.image_url && (
            <div className="mt-4 rounded-2xl overflow-hidden bg-black/20 border border-white/10">
              <img src={flash.image_url} alt="" className="w-full max-h-64 object-cover" data-testid="admin-flash-image" />
            </div>
          )}

          {flash.body && (
            <p className="mt-4 text-sm text-white/85 whitespace-pre-wrap" data-testid="admin-flash-body">
              {flash.body}
            </p>
          )}

          <div className="mt-6 flex gap-3">
            <Button
              type="button"
              onClick={handleClose}
              className="flex-1 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold"
              data-testid="admin-flash-dismiss"
            >
              Plus tard
            </Button>
            {flash.cta_label && (
              <Button
                type="button"
                onClick={handleCta}
                className="flex-1 h-12 rounded-full bg-[#FACC15] hover:bg-[#EAB308] text-[#0A2540] font-extrabold shadow-lg shadow-[#FACC15]/25"
                data-testid="admin-flash-cta"
              >
                {flash.cta_label} <ExternalLink className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>

          {flash.sent_by_name && (
            <p className="mt-4 text-[10px] text-white/40 text-right">Envoyé par {flash.sent_by_name}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
